import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ChefHat, Calculator, Camera, RefreshCw, Wand2, Loader2, ShoppingCart, Send,
  CheckCircle2, AlertTriangle, Clock, Thermometer, Archive, Replace, StickyNote, History, FileDown, Share2, Wine, DollarSign, Sparkles, GlassWater, X,
} from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Loader, Badge, DemoTag, fcTone } from "@/components/kit";

const MODIFY_CMDS = ["Make More Upscale", "Lower Food Cost", "Make Seasonal", "Simplify Preparation",
  "Change Protein", "Make Vegan", "Make Vegetarian", "Make Gluten-Free", "Use Inventory First",
  "Increase Profit", "Change Plating", "Replace Ingredient"];
const PLATING_STYLES = ["Classic", "Modern", "Fine Dining", "Minimalist", "Rustic", "Avant-Garde", "Luxury", "Contemporary"];
const VIEWS = [["45-degree", "45\u00B0"], ["top-down", "Top-Down"], ["chef-pov", "Chef POV"], ["close-up", "Close-Up"]];
const PLATING_CONTROLS = ["Change Plate", "Change Garnish", "Change Arrangement", "Change Sauce Placement"];

export default function MenuDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [menu, setMenu] = useState(null);
  const [sel, setSel] = useState(0);
  const [tab, setTab] = useState("recipe");
  const [busy, setBusy] = useState(null);
  const [nlMod, setNlMod] = useState("");
  const [showShare, setShowShare] = useState(false);
  const [shareEmail, setShareEmail] = useState("");
  const [sharing, setSharing] = useState(false);
  const [showAutoplate, setShowAutoplate] = useState(false);
  const [autoplating, setAutoplating] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [exportImages, setExportImages] = useState(true);
  const [showDrink, setShowDrink] = useState(false);
  const [drinkPrompt, setDrinkPrompt] = useState("");
  const [addingDrink, setAddingDrink] = useState(false);
  const [pairingAll, setPairingAll] = useState(false);
  const [unmatched, setUnmatched] = useState(null);
  const [menuAlerts, setMenuAlerts] = useState([]);

  const load = () => api.get(`/menus/${id}`).then(({ data }) => setMenu(data.menu)).catch((e) => toast.error(apiErr(e)));
  const loadExtras = () => {
    api.get(`/menus/${id}/unmatched`).then(({ data }) => setUnmatched(data)).catch(() => {});
    api.get("/price-alerts").then(({ data }) => setMenuAlerts((data.alerts || []).filter((a) => a.menu_id === id))).catch(() => {});
  };
  useEffect(() => { load(); loadExtras(); }, [id]);

  if (!menu) return <Loader label="Plating your menu…" />;
  const course = menu.courses[sel] || {};
  const pca = course.pca || {};

  const doModify = async (command, instruction) => {
    setBusy("modify");
    try {
      const { data } = await api.post(`/menus/${id}/courses/${course.id}/modify`, { command, instruction });
      setMenu(data.menu); toast.success("Course updated."); setNlMod("");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const doRegen = async () => {
    setBusy("regen");
    try { const { data } = await api.post(`/menus/${id}/courses/${course.id}/regenerate`); setMenu(data.menu); toast.success("Course regenerated."); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const doPlate = async (opts) => {
    setBusy("plate");
    try {
      const { data } = await api.post(`/menus/${id}/courses/${course.id}/plating`, opts);
      const m = { ...menu }; m.courses[sel] = data.course; setMenu(m); toast.success("Plating visualized.");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const saveNotes = async (note) => { await api.post(`/menus/${id}/courses/${course.id}/notes`, { note }); toast.success("Notes saved."); };
  const buildPurchasing = async () => {
    setBusy("purchase");
    try { await api.post("/purchasing/generate", { menu_id: id }); toast.success("Purchasing list built."); nav("/purchasing"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(null); }
  };
  const approve = async () => {
    const { data } = await api.post(`/menus/${id}/approve`);
    setMenu(data.menu); toast.success("Menu approved.");
    if (data.menu.courses.some((c) => !c.plating_image)) setShowAutoplate(true);
  };
  const runAutoplate = async () => {
    setShowAutoplate(false); setAutoplating(true);
    try {
      const { data } = await api.post(`/menus/${id}/autoplate`);
      toast.success(data.message);
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        const { data: m } = await api.get(`/menus/${id}`);
        setMenu(m.menu);
        if (!m.menu.courses.some((c) => !c.plating_image) || tries > 12) { clearInterval(iv); setAutoplating(false); }
      }, 6000);
    } catch (e) { toast.error(apiErr(e)); setAutoplating(false); }
  };
  const shareMenu = async () => {
    setSharing(true);
    try { const { data } = await api.post(`/menus/${id}/share`, { email: shareEmail }); toast.success(data.message); setShowShare(false); setShareEmail(""); }
    catch (e) { toast.error(apiErr(e)); } finally { setSharing(false); }
  };
  const exportPdf = async (path, filename) => {
    try {
      const res = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Download started."); setShowExport(false);
    } catch (e) { toast.error(apiErr(e)); }
  };
  const plateAll = () => { setShowAutoplate(false); runAutoplate(); };
  const reprice = async () => {
    try { const { data } = await api.post(`/menus/${id}/reprice`); setMenu(data.menu); toast.success(data.message); loadExtras(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const addDrink = async () => {
    setAddingDrink(true);
    try { const { data } = await api.post(`/menus/${id}/add-drink`, { instruction: drinkPrompt }); setMenu(data.menu); toast.success("Beverage added to the menu."); setShowDrink(false); setDrinkPrompt(""); }
    catch (e) { toast.error(apiErr(e)); } finally { setAddingDrink(false); }
  };
  const pairAll = async () => {
    setPairingAll(true);
    const baseBev = menu.courses.filter((c) => c.course === "Beverage" && c.pairs_with).length;
    try {
      const { data } = await api.post(`/menus/${id}/pair-all`);
      if (data.status !== "running") { toast.success(data.message); setPairingAll(false); return; }
      toast.info(data.message);
      let tries = 0;
      const iv = setInterval(async () => {
        tries += 1;
        try {
          const { data: m } = await api.get(`/menus/${id}`);
          setMenu(m.menu);
          const bev = m.menu.courses.filter((c) => c.course === "Beverage" && c.pairs_with).length;
          const status = m.menu.pairing_status;
          if (bev > baseBev || status === "done" || status === "error" || tries > 24) {
            clearInterval(iv); setPairingAll(false);
            if (status === "error") toast.error("Could not create pairings. Please try again.");
            else if (bev > baseBev) toast.success(`Paired ${bev - baseBev} course(s) with a beverage.`);
          }
        } catch { /* keep polling */ }
      }, 5000);
    } catch (e) { toast.error(apiErr(e)); setPairingAll(false); }
  };
  const dismissAlert = async (aid) => {
    try { await api.post(`/price-alerts/${aid}/ack`); setMenuAlerts((a) => a.filter((x) => x.id !== aid)); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <p className="label-eyebrow">{menu.cuisine} · {menu.service_style}</p>
          {menu.demo && <DemoTag />}
          <Badge tone={menu.status === "approved" ? "green" : "muted"}>{menu.status}</Badge>
        </div>
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5">
          <div className="max-w-2xl">
            <h1 className="font-serif text-4xl md:text-5xl leading-none">{menu.menu_name}</h1>
            <p className="text-white/55 mt-3">{menu.concept}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setShowShare(true)} data-testid="share-menu-btn" className="btn-outline-gold px-4 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
              <Share2 size={14} /> Share
            </button>
            <button onClick={() => setShowDrink(true)} data-testid="add-drink-btn" className="btn-outline-gold px-4 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
              <Wine size={14} /> Add Drink
            </button>
            <button onClick={pairAll} disabled={pairingAll} data-testid="pair-all-btn" className="btn-outline-gold px-4 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
              {pairingAll ? <Loader2 size={14} className="animate-spin" /> : <GlassWater size={14} />} {pairingAll ? "Pairing…" : "Pair Courses"}
            </button>
            <button onClick={plateAll} data-testid="plate-all-btn" className="btn-outline-gold px-4 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
              <Sparkles size={14} /> Plate All
            </button>
            <button onClick={reprice} data-testid="reprice-btn" className="btn-outline-gold px-4 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
              <DollarSign size={14} /> Sysco Pricing
            </button>
            <button onClick={() => setShowExport(true)} data-testid="export-open-btn" className="btn-outline-gold px-4 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
              <FileDown size={14} /> Export
            </button>
            <button onClick={buildPurchasing} disabled={busy === "purchase"} data-testid="build-purchasing-btn" className="btn-outline-gold px-4 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
              {busy === "purchase" ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />} Purchasing
            </button>
            <button onClick={() => nav(`/kitchen-mode/${id}`)} data-testid="send-kitchen-btn" className="btn-outline-gold px-5 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2">
              <Send size={14} /> Send to Kitchen
            </button>
            {menu.status !== "approved" && <button onClick={approve} data-testid="approve-menu-btn" className="btn-gold px-4 py-3 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2"><CheckCircle2 size={14} /> Approve</button>}
          </div>
        </div>
      </div>

      {autoplating && (
        <div className="mb-4 flex items-center gap-2 text-sm text-gold bg-gold/10 border border-gold/30 rounded-sm px-4 py-2.5">
          <Loader2 size={15} className="animate-spin" /> Generating plating photos for courses that need one… they'll appear automatically.
        </div>
      )}
      {menuAlerts.length > 0 && (
        <div className="mb-4 rounded-sm bg-red-500/10 border border-red-500/30 p-4" data-testid="menu-price-alerts">
          <p className="text-sm text-red-200 flex items-center gap-2 mb-2"><AlertTriangle size={16} /> Food cost alert — {menuAlerts.length} dish{menuAlerts.length > 1 ? "es" : ""} exceed target after a pricing update</p>
          <div className="space-y-1.5">
            {menuAlerts.map((a) => (
              <div key={a.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-white/75">{a.course} — <span className="font-mono text-red-300">{a.new_food_cost_pct}%</span> <span className="text-white/40">vs {a.target}% target · ${a.old_cost} → ${a.new_cost}</span></span>
                <button onClick={() => dismissAlert(a.id)} data-testid={`menu-dismiss-alert-${a.id}`} className="text-white/40 hover:text-white shrink-0"><X size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      )}
      {unmatched?.imported && unmatched.total > 0 && (
        <div className="mb-4 rounded-sm bg-gold/10 border border-gold/30 p-4 text-sm text-gold/90" data-testid="menu-unmatched-notice">
          <p className="flex items-center gap-2"><AlertTriangle size={15} /> {unmatched.total} ingredient{unmatched.total > 1 ? "s" : ""} not yet matched to your Order Guide — pricing for {unmatched.courses.map((c) => c.course).join(", ")} is still estimated. Use “Sysco Pricing” after adding them to your guide.</p>
        </div>
      )}
      {/* Menu economics strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <MiniStat label="Guests" value={menu.guests} />
        <MiniStat label="Total Plate Cost" value={`$${menu.total_cost}`} />
        <MiniStat label="Revenue Potential" value={`$${menu.revenue_potential?.toLocaleString?.()}`} accent />
        <MiniStat label="Target Food Cost" value={`${menu.target_food_cost}%`} />
      </div>

      <div className="grid lg:grid-cols-[260px_1fr] gap-6">
        {/* Course rail */}
        <div className="space-y-2">
          {menu.courses.map((c, i) => (
            <button key={c.id} onClick={() => { setSel(i); }} data-testid={`course-tab-${i}`}
              className={`w-full text-left p-4 rounded-sm border transition-all ${i === sel ? "border-gold bg-gold/10" : "border-white/10 hover:border-white/25"}`}>
              <p className="label-eyebrow">{c.course}</p>
              <p className={`font-serif text-lg mt-1 leading-tight ${i === sel ? "gold-text" : "text-white/90"}`}>{c.name}</p>
              {c.course === "Beverage" && c.pairs_with && (
                <p className="text-white/40 text-xs mt-1 flex items-center gap-1"><GlassWater size={11} className="text-gold/70" /> Pairs with {c.pairs_with}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <Badge tone={fcTone(c.pca?.food_cost_pct, menu.target_food_cost)}>{c.pca?.food_cost_pct}% FC</Badge>
                <span className="font-mono text-white/40 text-xs">${c.pca?.total_cost}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div>
          <div className="flex gap-2 mb-5">
            {[["recipe", "Recipe", ChefHat], ["pca", "PCA", Calculator], ["plate", "Plate Studio", Camera]].map(([k, l, Ic]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`detail-tab-${k}`}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-sm text-sm border transition-colors ${tab === k ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/60 hover:border-white/25"}`}>
                <Ic size={15} /> {l}
              </button>
            ))}
          </div>

          <motion.div key={tab + sel} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            {tab === "recipe" && <RecipeCard course={course} menu={menu} onSaveNotes={saveNotes} />}
            {tab === "pca" && <PCAPanel course={course} menu={menu} onModify={doModify} busy={busy} />}
            {tab === "plate" && <PlatePanel course={course} onPlate={doPlate} busy={busy} />}
          </motion.div>

          {/* AI modify bar */}
          <div className="matte-card p-5 mt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="label-eyebrow flex items-center gap-2"><Wand2 size={14} className="text-gold" /> Modify this course</p>
              <button onClick={doRegen} disabled={busy === "regen"} data-testid="regenerate-course-btn" className="text-gold text-sm hover:underline flex items-center gap-1">
                {busy === "regen" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Regenerate course
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mb-3">
              {MODIFY_CMDS.map((cmd) => (
                <button key={cmd} onClick={() => doModify(cmd)} disabled={busy === "modify"} data-testid={`modify-${cmd.split(" ")[1]?.toLowerCase() || cmd.toLowerCase()}`}
                  className="px-3 py-2 rounded-sm text-xs border border-white/10 text-white/60 hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-40">{cmd}</button>
              ))}
            </div>
            <div className="flex gap-2">
              <input value={nlMod} onChange={(e) => setNlMod(e.target.value)} data-testid="nl-modify-input"
                placeholder="Or describe a change… e.g. add a smoked element and a bright acid"
                className="flex-1 bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60 text-sm"
                onKeyDown={(e) => e.key === "Enter" && nlMod && doModify("custom", nlMod)} />
              <button onClick={() => nlMod && doModify("custom", nlMod)} disabled={busy === "modify"} className="btn-gold px-5 text-xs uppercase tracking-widest rounded-sm">
                {busy === "modify" ? <Loader2 size={14} className="animate-spin" /> : "Apply"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {showShare && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setShowShare(false)}>
          <div className="matte-card p-7 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl flex items-center gap-2"><Share2 size={20} className="text-gold" /> Share this menu</h3>
            <p className="text-white/50 text-sm mt-2 mb-5">Email a read-only preview of "{menu.menu_name}" to a client or partner. No account required to view.</p>
            <input type="email" placeholder="recipient@email.com" value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} data-testid="share-email-input"
              className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 mb-4 text-white/90 outline-none focus:border-gold/60" />
            <button onClick={shareMenu} disabled={sharing || !shareEmail} data-testid="share-send-btn" className="btn-gold w-full py-3.5 text-xs uppercase tracking-widest rounded-sm">
              {sharing ? "Sending…" : "Send Preview"}
            </button>
          </div>
        </div>
      )}
      {showExport && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setShowExport(false)}>
          <div className="matte-card p-7 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl flex items-center gap-2"><FileDown size={20} className="text-gold" /> Export</h3>
            <label className="flex items-center gap-2 mt-4 mb-5 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={exportImages} onChange={(e) => setExportImages(e.target.checked)} data-testid="export-images-toggle" className="accent-[#D4AF37]" />
              Include plating images
            </label>
            <div className="space-y-2">
              <button onClick={() => exportPdf(`/menus/${id}/export/pdf?variant=recipes&images=${exportImages}`, `${menu.menu_name} - Recipes.pdf`)} data-testid="export-recipes-btn" className="btn-gold w-full py-3 text-xs uppercase tracking-widest rounded-sm">Download Recipes {exportImages ? "(with images)" : ""}</button>
              <button onClick={() => exportPdf(`/menus/${id}/export/pdf?variant=menu&images=${exportImages}`, `${menu.menu_name} - Menu.pdf`)} data-testid="export-menu-btn" className="btn-outline-gold w-full py-3 text-xs uppercase tracking-widest rounded-sm">Download Menu Card {exportImages ? "(with images)" : ""}</button>
              <button onClick={() => exportPdf(`/menus/${id}/export/pca`, `${menu.menu_name} - PCA.pdf`)} data-testid="export-pca-btn" className="btn-outline-gold w-full py-3 text-xs uppercase tracking-widest rounded-sm">Download PCA (cost analysis)</button>
            </div>
          </div>
        </div>
      )}

      {showDrink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setShowDrink(false)}>
          <div className="matte-card p-7 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-serif text-2xl flex items-center gap-2"><Wine size={20} className="text-gold" /> Add a beverage</h3>
            <p className="text-white/50 text-sm mt-2 mb-4">Generate a cocktail, mocktail or pairing that complements this menu.</p>
            <input value={drinkPrompt} onChange={(e) => setDrinkPrompt(e.target.value)} data-testid="drink-input"
              placeholder="e.g. a smoky bourbon cocktail for the short rib course"
              className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 mb-4 text-white/90 outline-none focus:border-gold/60" />
            <button onClick={addDrink} disabled={addingDrink} data-testid="drink-generate-btn" className="btn-gold w-full py-3.5 text-xs uppercase tracking-widest rounded-sm flex items-center justify-center gap-2">
              {addingDrink ? <Loader2 size={15} className="animate-spin" /> : <Wine size={15} />} {addingDrink ? "Mixing…" : "Generate Beverage"}
            </button>
          </div>
        </div>
      )}

      {showAutoplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-6" onClick={() => setShowAutoplate(false)}>
          <div className="matte-card p-7 w-full max-w-md text-center" onClick={(e) => e.stopPropagation()}>
            <div className="h-14 w-14 rounded-sm bg-gold/10 border border-gold/30 flex items-center justify-center text-gold mx-auto mb-4"><Camera size={24} /></div>
            <h3 className="font-serif text-2xl">Menu approved — plate it?</h3>
            <p className="text-white/50 text-sm mt-2 mb-6">Some courses don't have a plating photo yet. Want Chef Plated to generate them automatically so this menu looks finished everywhere?</p>
            <div className="flex gap-2">
              <button onClick={() => setShowAutoplate(false)} className="flex-1 py-3 text-sm text-white/60 hover:text-white border border-white/10 rounded-sm">Not now</button>
              <button onClick={runAutoplate} data-testid="autoplate-confirm-btn" className="flex-1 btn-gold py-3 text-xs uppercase tracking-widest rounded-sm">Generate Plating</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, accent }) {
  return <div className="matte-card px-5 py-4"><p className="label-eyebrow">{label}</p><p className={`font-mono text-xl mt-1 ${accent ? "gold-text" : "text-cream"}`}>{value}</p></div>;
}

function RecipeCard({ course, menu, onSaveNotes }) {
  const [notes, setNotes] = useState(course.chef_notes || "");
  useEffect(() => setNotes(course.chef_notes || ""), [course.id]);
  return (
    <div className="matte-card overflow-hidden">
      {course.plating_image && <div className="h-56 md:h-72 relative"><img src={course.plating_image} alt={course.name} className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-t from-surface to-transparent" /></div>}
      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div><p className="label-eyebrow">{course.course} · {course.cuisine}</p><h2 className="font-serif text-3xl mt-1">{course.name}</h2></div>
          <Badge>{course.difficulty}</Badge>
        </div>
        <p className="text-white/60 mt-3">{course.description}</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 text-sm">
          <Meta icon={ChefHat} label="Yield" value={course.yield} />
          <Meta icon={Clock} label="Prep" value={course.prep_time} />
          <Meta icon={Clock} label="Cook" value={course.cook_time} />
          <Meta icon={Clock} label="Total" value={course.total_time} />
        </div>

        <Section title="Ingredients">
          <div className="divide-y divide-white/5">
            {(course.ingredients || []).map((ing, i) => (
              <div key={i} className="flex items-baseline justify-between py-2.5 gap-3">
                <div className="min-w-0"><span className="text-white/90">{ing.name}</span>{ing.prep && <span className="text-white/40 text-sm"> · {ing.prep}</span>}
                  <p className="text-white/30 text-xs">{ing.supplier_item} · {ing.pack_size}</p></div>
                <span className="font-mono text-white/70 text-sm whitespace-nowrap">{ing.quantity} {ing.unit}</span>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Method">
          <ol className="space-y-3">
            {(course.method || []).map((m, i) => (
              <li key={i} className="flex gap-3"><span className="font-mono text-gold text-sm shrink-0">{String(i + 1).padStart(2, "0")}</span><span className="text-white/70">{m}</span></li>
            ))}
          </ol>
        </Section>

        <div className="grid md:grid-cols-2 gap-x-8">
          <Section title="Mise en Place"><ul className="space-y-1.5 text-white/70 text-sm list-disc list-inside">{(course.mise_en_place || []).map((m, i) => <li key={i}>{m}</li>)}</ul></Section>
          <Section title="Substitutions"><ul className="space-y-1.5 text-white/70 text-sm">{(course.substitutions || []).map((s, i) => <li key={i} className="flex items-center gap-2"><Replace size={13} className="text-gold/60" />{s.ingredient} → {s.alt}</li>)}</ul></Section>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-5">
          <InfoBox icon={Archive} title="Storage" body={course.storage} />
          <InfoBox icon={Thermometer} title="Holding" body={course.holding} />
        </div>

        <div className="mt-5 p-4 rounded-sm bg-red-500/5 border border-red-500/20 flex gap-3">
          <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-red-300 text-sm font-medium">Allergens: {(course.allergens || []).join(", ") || "None identified"}</p>
            <p className="text-white/40 text-xs mt-1">AI-identified. Always verify product labels and kitchen cross-contact procedures.</p>
          </div>
        </div>

        {course.chef_tip && <div className="mt-4 p-4 rounded-sm bg-gold/5 border border-gold/20 text-sm text-white/70"><span className="text-gold">Chef tip: </span>{course.chef_tip}</div>}

        <Section title="Chef Notes">
          <textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="chef-notes-input"
            placeholder="Private notes for this recipe…"
            className="w-full bg-surface border border-white/10 rounded-sm px-4 py-3 text-white/90 outline-none focus:border-gold/60 resize-none text-sm" />
          <button onClick={() => onSaveNotes(notes)} data-testid="save-notes-btn" className="btn-outline-gold mt-2 px-5 py-2 text-xs uppercase tracking-widest rounded-sm flex items-center gap-2"><StickyNote size={13} /> Save Notes</button>
        </Section>

        {course.versions?.length > 0 && (
          <Section title={`Version History (${course.versions.length})`}>
            <div className="space-y-2">{course.versions.map((v, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-white/50"><History size={13} className="text-gold/60" /> {v.name} · saved {new Date(v.saved_at).toLocaleString()}</div>
            ))}</div>
          </Section>
        )}
      </div>
    </div>
  );
}

function PCAPanel({ course, menu, onModify, busy }) {
  const pca = course.pca || {};
  const [target, setTarget] = useState(menu.target_food_cost);
  const [sugg, setSugg] = useState([]);
  const suggest = async () => {
    try { const { data } = await api.post(`/menus/${menu.id}/courses/${course.id}/target-cost`, { target_food_cost: +target }); setSugg(data.suggestions); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="matte-card p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-serif text-2xl">Plate Cost Analysis</h2>
        <Badge tone={fcTone(pca.food_cost_pct, menu.target_food_cost)}>{pca.food_cost_pct}% food cost</Badge>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left label-eyebrow border-b border-white/10">
            <th className="py-2 pr-3">Ingredient</th><th className="py-2 px-3">SKU</th><th className="py-2 px-3">Pack</th>
            <th className="py-2 px-3 text-right">Price</th><th className="py-2 px-3 text-right">Qty</th><th className="py-2 px-3 text-right">Yield</th><th className="py-2 pl-3 text-right">Cost</th>
          </tr></thead>
          <tbody className="font-mono">
            {(pca.rows || []).map((r, i) => (
              <tr key={i} className="border-b border-white/5 hover:bg-black/40">
                <td className="py-2.5 pr-3 font-sans text-white/85">{r.ingredient}</td>
                <td className="py-2.5 px-3 text-white/40 text-xs">{r.sku}</td>
                <td className="py-2.5 px-3 text-white/50 text-xs">{r.pack_size}</td>
                <td className="py-2.5 px-3 text-right text-white/60">${r.purchase_price}</td>
                <td className="py-2.5 px-3 text-right text-white/60">{r.recipe_quantity}{r.unit}</td>
                <td className="py-2.5 px-3 text-right text-white/60">{r.usable_yield}%</td>
                <td className="py-2.5 pl-3 text-right text-gold">${r.recipe_cost}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
        <MiniStat label="Plate Cost" value={`$${pca.total_cost}`} />
        <MiniStat label="Suggested Price" value={`$${pca.suggested_price}`} accent />
        <MiniStat label="Gross Profit" value={`$${pca.gross_profit}`} />
        <MiniStat label="Gross Margin" value={`${pca.gross_margin}%`} />
      </div>
      <div className="mt-6 p-5 rounded-sm bg-black/40 border border-white/10">
        <p className="label-eyebrow mb-3">Target Cost Optimizer</p>
        <div className="flex flex-wrap items-end gap-3">
          <div><label className="text-white/40 text-xs">Target FC %</label>
            <input type="number" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="target-cost-input"
              className="block mt-1 w-28 bg-surface border border-white/10 rounded-sm px-3 py-2 text-white/90 outline-none focus:border-gold/60 font-mono" /></div>
          <div className="text-sm"><span className="text-white/40">Current: </span><span className="font-mono text-gold">{pca.food_cost_pct}%</span></div>
          <button onClick={suggest} data-testid="suggest-optimization-btn" className="btn-outline-gold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm">Suggest Adjustments</button>
        </div>
        {sugg.length > 0 && (
          <div className="mt-4 space-y-2">
            {sugg.map((s, i) => <p key={i} className="text-white/70 text-sm flex gap-2"><Wand2 size={14} className="text-gold shrink-0 mt-0.5" /> {s}</p>)}
            <button onClick={() => onModify("Lower Food Cost")} disabled={busy === "modify"} className="btn-gold mt-2 px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm">
              {busy === "modify" ? "Applying…" : "Apply Optimization (with approval)"}
            </button>
            <p className="text-white/30 text-xs">Chef Plated never silently alters recipes. Review changes before approving.</p>
          </div>
        )}
      </div>
      <p className="text-white/30 text-xs mt-4">Estimated costs from {menu.demo ? "demo" : "reference"} supplier pricing. Connect Sysco for live account pricing.</p>
    </div>
  );
}

function PlatePanel({ course, onPlate, busy }) {
  const [style, setStyle] = useState(course.plating_style || "Fine Dining");
  const [view, setView] = useState("45-degree");
  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="matte-card overflow-hidden">
        <div className="aspect-square bg-black/50 flex items-center justify-center relative">
          {course.plating_image
            ? <img src={course.plating_image} alt={course.name} className="w-full h-full object-cover" />
            : <div className="text-center text-white/30 p-8"><Camera size={40} className="mx-auto mb-3" /><p className="text-sm">No plating yet. Generate a visual from the actual ingredients.</p></div>}
          {busy === "plate" && <div className="absolute inset-0 bg-black/70 flex flex-col items-center justify-center"><Loader2 size={30} className="animate-spin text-gold mb-3" /><p className="text-white/60 text-sm">Designing the plate…</p></div>}
        </div>
      </div>
      <div className="matte-card p-6">
        <h3 className="font-serif text-2xl mb-1">Plate Studio</h3>
        <p className="text-white/50 text-sm mb-5">Visual built from this dish's ingredients & preparation.</p>
        <label className="label-eyebrow block mb-2">Plating style</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {PLATING_STYLES.map((s) => <button key={s} onClick={() => setStyle(s)} data-testid={`plate-style-${s.split(" ")[0].toLowerCase()}`}
            className={`px-3 py-2 rounded-sm text-xs border transition-colors ${style === s ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/60 hover:border-white/25"}`}>{s}</button>)}
        </div>
        <label className="label-eyebrow block mb-2">Camera angle</label>
        <div className="flex flex-wrap gap-2 mb-4">
          {VIEWS.map(([v, l]) => <button key={v} onClick={() => setView(v)}
            className={`px-3 py-2 rounded-sm text-xs border transition-colors ${view === v ? "border-gold bg-gold/10 text-gold" : "border-white/10 text-white/60 hover:border-white/25"}`}>{l}</button>)}
        </div>
        <button onClick={() => onPlate({ style, view })} disabled={busy === "plate"} data-testid="generate-plating-btn"
          className="btn-gold w-full py-3.5 text-sm uppercase tracking-widest rounded-sm flex items-center justify-center gap-2">
          <Camera size={15} /> {course.plating_image ? "Regenerate Plate" : "Generate Plate"}
        </button>
        <div className="flex flex-wrap gap-2 mt-3">
          {PLATING_CONTROLS.map((c) => <button key={c} onClick={() => onPlate({ style, view, control: c })} disabled={busy === "plate"}
            className="px-3 py-1.5 rounded-sm text-xs border border-white/10 text-white/50 hover:border-gold/50 hover:text-gold disabled:opacity-40">{c}</button>)}
        </div>
        <div className="mt-6 pt-5 border-t border-white/5">
          <p className="label-eyebrow mb-3">Plating Map</p>
          <ol className="space-y-2">
            {(course.plating_map || []).map((s, i) => <li key={i} className="flex gap-3 text-sm text-white/70"><span className="font-mono text-gold">{i + 1}</span> {s}</li>)}
          </ol>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return <div className="mt-6 pt-6 border-t border-white/5"><p className="label-eyebrow mb-3">{title}</p>{children}</div>;
}
function Meta({ icon: Icon, label, value }) {
  return <div className="flex items-center gap-2"><Icon size={15} className="text-gold/60" /><div><p className="text-white/40 text-xs">{label}</p><p className="text-white/80">{value}</p></div></div>;
}
function InfoBox({ icon: Icon, title, body }) {
  return <div className="p-4 rounded-sm bg-black/30 border border-white/5"><p className="flex items-center gap-2 text-white/80 text-sm font-medium"><Icon size={15} className="text-gold/60" /> {title}</p><p className="text-white/50 text-sm mt-1.5">{body}</p></div>;
}
